<style scoped>
.order-svg:hover {
    transform: rotate(-20deg);
}
</style>

<template>
    <div>
        <breadcrumb class="mt-4" :links="[{ msg: 'order.history' }]" />

        <div class="mt-3">
            <div class="text-secondary">
                <button
                    class="px-2 focus:outline-none border-r border-secondary"
                    :class="{
                        'text-darkGrayishBlue': ordHistWithinDays === 30
                    }"
                    @click="ordHistWithinDays = 30"
                >
                    {{ '最近一個月' }}
                </button>
                <button
                    class="px-2 focus:outline-none "
                    :class="{
                        'text-darkGrayishBlue': ordHistWithinDays === undefined
                    }"
                    @click="ordHistWithinDays = undefined"
                >
                    {{ '全部訂單' }}
                </button>
            </div>
        </div>

        <div class="my-8 flex justify-center" v-if="loading">
            <spinner />
        </div>

        <div v-else-if="hasOrder" class="content-box-wrapper">
            <div class="text-xl font-bold">{{ $t('order.history') }}</div>

            <div class="w-full flex flex-col">
                <div
                    v-for="(o, oi) in recordsInPage"
                    :key="oi"
                    class="w-full py-8 border-b border-dotted border-gray-100 flex flex-col sm:flex-row sm:items-center"
                >
                    <div class="w-full sm:w-1/2 md:w-2/5 flex-shrink-0 ">
                        <div class="my-1">
                            {{ $t('order.date') }}
                            :
                            {{ o.saleOrdCreDate | formatDate('fullDateTime') }}
                        </div>
                        <div class="my-1 flex items-center">
                            {{ $t('order.number') }}
                            :
                            {{ o.saleOrdNum }}
                            <router-link
                                :to="{
                                    name: 'order',
                                    params: { orderNo: o.saleOrdTxnNum }
                                }"
                                class="icon text-primary cursor-pointer"
                            >
                                <docSvg class="order-svg" />
                            </router-link>
                        </div>
                        <div class="my-1">
                            {{ $t('cart.totalPrice') }}
                            :
                            {{ o.pymtTotAmt | price(true) }}
                        </div>
                        <div class="my-1">
                            {{ $t('order.status') }}
                            :
                            {{ $t('order.status.' + o.saleOrdStsCode) }}
                        </div>
                        <div class="my-1" v-if="o.saleOrdStsCode === 'C'">
                            {{ $t('order.dlvr.status') }}
                            :
                            {{
                                $t(
                                    'order.dlvr.status.' + dlvrStsCode(o),
                                    dlvrDate(o)
                                )
                            }}
                        </div>
                    </div>
                    <div class="flex-grow flex flex-col items-stretch">
                        <productCarousel
                            :list="
                                o.saleOrdProdList.filter(p => !p.bndlProdKey)
                            "
                            productListClass=""
                            productWidthClass="w-1/3 md:w-1/4 lg:w-1/6 p-2"
                            :getNumOfProductInCurrentScreenSize="
                                screen => {
                                    if (screen === 'lg' || screen === 'xl') {
                                        return 6;
                                    }
                                    if (screen === 'md') {
                                        return 4;
                                    }
                                    return 3;
                                }
                            "
                        >
                            <template #product="{product}">
                                <router-link
                                    :to="{
                                        name: 'productInfo',
                                        params: { prodId: product.prodId }
                                    }"
                                >
                                    <productImage
                                        imageType="small"
                                        :product="product"
                                        class="w-full"
                                    />
                                </router-link>
                            </template>
                        </productCarousel>

                        <router-link
                            :to="{
                                name: 'order',
                                params: { orderNo: o.saleOrdTxnNum }
                            }"
                            class="self-center"
                        >
                            <span class="text-secondary text-sm underline">
                                {{ '查看所有產品' }}
                            </span>
                        </router-link>
                    </div>
                </div>
            </div>
            <div class="w-full flex justify-center mt-4">
                <pager
                    class="mx-auto"
                    :page="pager.page"
                    :recordsPerPage="pager.recordsPerPage"
                    :totalRecords="list.length"
                    @setPage="p => (pager.page = p)"
                />
            </div>
        </div>

        <div class="flex items-center justify-center pt-4" v-else>
            <img :src="sentimentImg" class="w-24" />
            <msgSlot msgKey="order.no.order.history" class="text-2xl ">
                <template #slot0>
                    <router-link class="text-blue underline" :to="'/'">
                        {{ $t('order.no.order.history.arg0') }}
                    </router-link>
                </template>
            </msgSlot>
        </div>
    </div>
</template>

<script>
import breadcrumb from '@/components/breadcrumb.vue';
import formatDate from '@/components/formatDateFilter';
import price from '@/components/priceFilter';
import spinner from '@/components/spinner.vue';
import productCarousel from '../../components/productCarousel.vue';
import productImage from '@/components/productImage.vue';
import docSvg from '../../assets/svg/document.svg';
import pager from '../../components/pager.vue';
import msgSlot from '../../components/msgSlot';
import img6s112 from '../../assets/img/6-112.png';

export default {
    components: {
        breadcrumb,
        spinner,
        productCarousel,
        productImage,
        docSvg,
        pager,
        msgSlot
    },
    filters: { price, formatDate },
    data() {
        return {
            loading: true,
            pager: {
                page: 1,
                recordsPerPage: 10
            },
            sentimentImg: img6s112,
            ordHistWithinDays: 30
        };
    },
    async mounted() {
        this.loading = true;
        await this.$store.dispatch('loadOrderList', this.ordHistWithinDays);
        this.loading = false;

        this.$watch('ordHistWithinDays', async function() {
            this.loading = true;
            await this.$store.dispatch('loadOrderList', this.ordHistWithinDays);
            this.loading = false;
        });
    },
    computed: {
        list() {
            return this.$store.state.orderList.filter(
                ord => ord.saleOrdTypeCode === 'SALE'
            );
        },
        recordsInPage() {
            const start = (this.pager.page - 1) * this.pager.recordsPerPage;
            return this.list.slice(start, start + this.pager.recordsPerPage);
        },
        priceTotal() {
            return this.$store.getters.totalPrice;
        },
        hasOrder() {
            return this.list && this.list.length > 0;
        },
        dlvrStsCode() {
            return saleOrd => {
                return saleOrd.pickOrdKey
                    ? !saleOrd.pickOrdActlDlvrDate
                        ? saleOrd.pickOrdSchdDlvrDate
                            ? 'scheduled'
                            : 'picking'
                        : 'success'
                    : 'pending';
            };
        },
        dlvrDate() {
            return saleOrd => {
                if (this.dlvrStsCode(saleOrd) === 'success')
                    return formatDate(saleOrd.pickOrdActlDlvrDate, 'fulldate');
                else if (this.dlvrStsCode(saleOrd) === 'scheduled')
                    return formatDate(saleOrd.pickOrdSchdDlvrDate, 'fulldate');
            };
        }
    }
};
</script>
